import { FC, ReactNode, useRef } from 'react';

import { BannerStatistics, getBannerTextTypography } from 'utils/banner/utils';
import { getFormattedNumberWithPercent } from 'utils/clickme';
import { CampaignSource } from 'utils/constants/campaign';
import { DATE_FORMAT_DD_MM_YY } from 'utils/constants/date';
import { formatDate } from 'utils/date';

import { BottomSheet, Card, Flex, Text, TextList, TooltipHover, useBreakpoint } from '@hh.ru/magritte-ui';
import { QuestionCircleOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { BANNER_SOURCE_ICONS } from 'components/Banners/components/Banner/sourceIcons';
import { EMPTY_VALUE } from 'components/CampaignsDashboard/utils/constants';

interface BannerStatisticsCardProps {
    statistics: BannerStatistics;
    createdAt: string;
    sources: CampaignSource[];
}

interface StatisticsCellProps {
    label: string;
    value: ReactNode;
    tooltip?: string;
}

const TrlKeys = {
    shows: 'BannersPage.banner.statistics.shows',
    clicks: 'BannersPage.banner.statistics.clicks',
    ctr: 'BannersPage.banner.statistics.ctr',
    sources: 'BannersPage.banner.statistics.sources',
    createdAt: 'BannersPage.banner.statistics.createdAt',
    tooltip: 'BannersPage.banner.statistics.ctrTooltip',
};

const StatisticsCell: FC<StatisticsCellProps> = ({ label, value, tooltip }) => {
    const [isTooltipVisible, setIsTooltipVisibleOn, setIsTooltipVisibleOff] = useOnOffState(false);

    const { isMobile } = useBreakpoint();
    const tooltipRef = useRef(null);

    return (
        <Flex
            direction={isMobile ? 'row' : 'column'}
            justify={isMobile ? 'space-between' : 'start'}
            gap={6}
            grow={1}
            basis={isMobile ? undefined : 0}
        >
            <Flex direction="row" gap={4} align="center">
                <Text style="secondary" typography={getBannerTextTypography(isMobile)} data-qa={label}>
                    {label}
                </Text>
                {tooltip && (
                    <QuestionCircleOutlinedSize16
                        onClick={setIsTooltipVisibleOn}
                        ref={tooltipRef}
                        initialColor="secondary"
                    />
                )}
            </Flex>
            {tooltip && (
                <>
                    <TooltipHover activatorRef={tooltipRef} placement="right-center">
                        {tooltip}
                    </TooltipHover>
                    <BottomSheet visible={isTooltipVisible} onClose={setIsTooltipVisibleOff}>
                        <Text typography="label-2-regular">{tooltip}</Text>
                    </BottomSheet>
                </>
            )}
            {value}
        </Flex>
    );
};

const BannerSourceIcon: FC<{ source: CampaignSource }> = ({ source }) => {
    const [isTooltipVisible, setIsTooltipVisibleOn, setIsTooltipVisibleOff] = useOnOffState(false);

    const { isMobile } = useBreakpoint();
    const trls = useTrls();
    const iconRef = useRef<HTMLImageElement>(null);

    const sourceIcons = BANNER_SOURCE_ICONS[source];

    if (!sourceIcons) {
        return null;
    }

    const { tooltipTrl, tooltipTrlList } = sourceIcons;

    const tooltipContent = (
        <>
            <Text>{trls(tooltipTrl)}</Text>
            {tooltipTrlList && (
                <TextList>
                    {tooltipTrlList.map((item) => (
                        <Text key={item}>{trls(item)}</Text>
                    ))}
                </TextList>
            )}
        </>
    );

    return (
        <>
            <img
                ref={iconRef}
                src={sourceIcons[isMobile ? 'mobile' : 'desktop']}
                alt={source}
                data-qa={`source-icon-${source}`}
                onClick={setIsTooltipVisibleOn}
            />
            <TooltipHover delay={500} placement="top-center" activatorRef={iconRef}>
                {tooltipContent}
            </TooltipHover>
            <BottomSheet visible={isTooltipVisible} onClose={setIsTooltipVisibleOff}>
                {tooltipContent}
            </BottomSheet>
        </>
    );
};

export const BannerStatisticsCard: FC<BannerStatisticsCardProps> = ({ statistics, createdAt, sources }) => {
    const { clicks, ctr, shows } = statistics;

    const trls = useTrls();
    const { isMobile } = useBreakpoint();

    return (
        <Card.Segment
            direction={isMobile ? 'column' : 'row'}
            p={isMobile ? 16 : 24}
            gap={isMobile ? 8 : 4}
            justify={isMobile ? 'start' : 'space-between'}
        >
            <StatisticsCell
                label={trls(TrlKeys.shows)}
                value={
                    <Text typography={isMobile ? 'label-3-regular' : 'title-4-semibold'} data-qa="shows">
                        {shows || EMPTY_VALUE}
                    </Text>
                }
            />
            <StatisticsCell
                label={trls(TrlKeys.clicks)}
                value={
                    <Text typography={isMobile ? 'label-3-regular' : 'title-4-semibold'} data-qa="clicks">
                        {clicks || EMPTY_VALUE}
                    </Text>
                }
            />
            <StatisticsCell
                label={trls(TrlKeys.ctr)}
                value={
                    <Text typography={isMobile ? 'label-3-regular' : 'title-4-semibold'} data-qa="ctr">
                        {ctr ? `${getFormattedNumberWithPercent(ctr * 100)}` : EMPTY_VALUE}
                    </Text>
                }
                tooltip={trls(TrlKeys.tooltip)}
            />
            <StatisticsCell
                label={trls(TrlKeys.sources)}
                value={
                    <Flex align="center" gap={4}>
                        {sources
                            ?.sort((a, b) => a.localeCompare(b))
                            .map((source) => (
                                <BannerSourceIcon key={source} source={source} />
                            ))}
                    </Flex>
                }
            />
            <StatisticsCell
                label={trls(TrlKeys.createdAt)}
                value={
                    <Text typography={isMobile ? 'label-3-regular' : 'title-4-semibold'} data-qa="created-at">
                        {formatDate(createdAt, DATE_FORMAT_DD_MM_YY)}
                    </Text>
                }
            />
        </Card.Segment>
    );
};
